// Frage der Woche: anlegen, Antworten anhören, freigeben, sortieren, O-Ton-Block bauen.
//
// Die Seite ist die Werkbank der Redaktion. Links steht, was an Fragen da ist;
// rechts der Eingang zur gewählten Frage: jede Antwort zum Anhören, mit Knöpfen
// zum Freigeben und Ablehnen, einem Stichwort und Pfeilen für die Reihenfolge.
// Ganz unten entsteht daraus mit einem Klick eine Datei in der Mediathek.
//
// Sichtbar ist die Seite nur, wenn das Modul „Frage der Woche“ eingeschaltet ist
// (Aussehen → Mitmach-Module).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { voiceSurveyApi, type VoiceSurvey, type VoiceSurveyAnswer, type VoiceSurveyDetail } from "../api";
import { api } from "../api";
import { useAuth } from "../auth/AuthProvider";
import { errorText } from "../components/streaming-ui";
import { KanalWahl, Meldungen, ModulAus, StandSchild, Sym } from "../components/social-ui";
import { Badge, Card, EmptyState, Page, PageHeader } from "../components/ui";
import type { Channel } from "../types";

/** Sekunden als 1:05. */
function mmss(ms: number | null): string {
  const s = Math.max(0, Math.round((ms ?? 0) / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

function datum(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }) : "–";
}

/** Aufnahme anhören. Lädt erst beim Klick – sonst holt der Eingang alle Aufnahmen auf einmal. */
function AnswerPlayer({ id }: { id: string }): JSX.Element {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);
  if (url) return <audio className="mt-2 h-10 w-full" src={url} controls autoPlay />;
  return (
    <div className="mt-2 flex items-center gap-2">
      <button
        onClick={() => voiceSurveyApi.answerAudioUrl(id).then(setUrl).catch((err) => setError(errorText(err)))}
        className="btn btn-secondary btn-sm"
      >
        <span className="flex h-4 w-4 items-center justify-center rounded-full bg-brand-primary text-white"><Sym name="play" className="h-2 w-2" /></span>
        Anhören
      </button>
      {error && <span className="text-xs text-red-600 dark:text-red-400">{error}</span>}
    </div>
  );
}

const STATUS_TEXT: Record<VoiceSurvey["status"], string> = {
  draft: "vorbereitet",
  open: "läuft",
  closed: "beendet",
};

/** Eine Antwort im Eingang. */
function AnswerRow({ answer, busy, rank, count, onPatch, onBlock, onMove }: {
  answer: VoiceSurveyAnswer;
  busy: boolean;
  /** Platz in der Reihenfolge des O-Ton-Blocks, oder null bei nicht freigegebenen. */
  rank: number | null;
  count: number;
  onPatch: (patch: { status?: "new" | "approved" | "rejected"; keyword?: string | null }) => void;
  onBlock: () => void;
  onMove: (direction: -1 | 1) => void;
}): JSX.Element {
  const [keyword, setKeyword] = useState(answer.keyword ?? "");
  useEffect(() => setKeyword(answer.keyword ?? ""), [answer.keyword]);

  // Stand der Antwort als farbige Kante links: neu = blau, freigegeben = grün, abgelehnt = blass.
  // (Als Schatten, nicht als Rand: die Trennlinien der Liste färben sonst alle Ränder um.)
  const kante = answer.status === "approved" ? "#10b981" : answer.status === "rejected" ? "transparent" : "#0ea5e9";
  return (
    <li className={`px-4 py-3.5 sm:px-5 ${answer.status === "rejected" ? "opacity-60" : ""}`} style={{ boxShadow: `inset 3px 0 0 ${kante}` }}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex min-w-0 items-center gap-2 font-medium text-primary">
          {rank !== null && (
            <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md bg-emerald-500/15 text-xs font-semibold tabular-nums text-emerald-700 dark:text-emerald-300">{rank + 1}</span>
          )}
          <span className="truncate">{answer.label}</span>
        </span>
        <span className="flex flex-wrap items-center gap-2 text-xs text-muted">
          <span className="inline-flex items-center gap-1 tabular-nums"><Sym name="clock" className="h-3.5 w-3.5" />{mmss(answer.duration_ms)}</span>
          <span className="tabular-nums">{datum(answer.created_at)}</span>
          {answer.status === "new" && <Badge tone="info" dot>neu</Badge>}
          {answer.status === "approved" && <Badge tone="success">freigegeben</Badge>}
          {answer.status === "rejected" && <Badge tone="neutral">abgelehnt</Badge>}
        </span>
      </div>

      {answer.has_audio ? <AnswerPlayer id={answer.id} /> : <p className="mt-2 text-xs text-muted">Die Aufnahme ist gelöscht.</p>}

      {/* Was der Hörer erlaubt hat – steht dort, wo entschieden wird. */}
      <p className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-muted">
        Einwilligung:
        <span className={answer.consent_broadcast_at ? "text-emerald-700 dark:text-emerald-300" : ""}>Senden {answer.consent_broadcast_at ? "ja" : "nein"}</span>
        ·
        <span className={answer.consent_public_at ? "text-emerald-700 dark:text-emerald-300" : ""}>Hörerseite {answer.consent_public_at ? "ja" : "nein"}</span>
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <input
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          onBlur={() => keyword !== (answer.keyword ?? "") && onPatch({ keyword: keyword.trim() || null })}
          maxLength={40}
          placeholder="Stichwort"
          className="input h-8 w-40 text-xs"
        />
        {answer.status !== "approved" && (
          <button disabled={busy || !answer.has_audio} onClick={() => onPatch({ status: "approved" })}
            className="btn btn-primary btn-sm"><Sym name="check" className="h-3.5 w-3.5" />Freigeben</button>
        )}
        {answer.status !== "rejected" && (
          <button disabled={busy} onClick={() => onPatch({ status: "rejected" })}
            className="btn btn-secondary btn-sm"
            title="Die Aufnahme wird sofort gelöscht">Ablehnen</button>
        )}
        {answer.status === "approved" && (
          <span className="inline-flex">
            <button disabled={busy || rank === 0} onClick={() => onMove(-1)}
              className="btn btn-secondary btn-sm btn-icon rounded-r-none" title="nach vorn" aria-label="nach vorn"><Sym name="up" className="h-3.5 w-3.5" /></button>
            <button disabled={busy || rank === null || rank >= count - 1} onClick={() => onMove(1)}
              className="btn btn-secondary btn-sm btn-icon -ml-px rounded-l-none" title="nach hinten" aria-label="nach hinten"><Sym name="down" className="h-3.5 w-3.5" /></button>
          </span>
        )}
        <button disabled={busy} onClick={onBlock}
          className="btn btn-ghost btn-sm ml-auto !text-red-600 dark:!text-red-400"
          title="Alle Antworten dieses Geräts ablehnen und es bis Mitternacht sperren"><Sym name="ban" className="h-3.5 w-3.5" />Gerät sperren</button>
      </div>
    </li>
  );
}

export function FrageDerWochePage(): JSX.Element {
  const { user } = useAuth();
  const mayRun = user?.role === "moderator" || user?.role === "tenant_admin" || user?.role === "super_admin";

  const [channels, setChannels] = useState<Channel[]>([]);
  const [slug, setSlug] = useState("");
  const [on, setOn] = useState<boolean | null>(null);
  const [maxSeconds, setMaxSeconds] = useState(45);
  const [publicAnswers, setPublicAnswers] = useState(false);
  const [list, setList] = useState<VoiceSurvey[]>([]);
  const [chosen, setChosen] = useState<string | null>(null);
  const [detail, setDetail] = useState<VoiceSurveyDetail | null>(null);
  const [question, setQuestion] = useState("");
  const [runDays, setRunDays] = useState(7);
  const [pauseMs, setPauseMs] = useState(700);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    api.listChannels().then((all) => {
      setChannels(all);
      setSlug((s) => s || all[0]?.slug || "");
    }).catch((err) => setError(errorText(err)));
    api.listModules().then((all) => {
      const mod = all.find((m) => m.id === "voice_survey");
      setOn(mod?.enabled === true);
      const settings = (mod?.settings ?? {}) as { max_seconds?: number; public_answers?: boolean };
      if (typeof settings.max_seconds === "number") setMaxSeconds(settings.max_seconds);
      setPublicAnswers(settings.public_answers === true);
    }).catch((err) => setError(errorText(err)));
  }, []);

  const load = useCallback(async (): Promise<void> => {
    if (!slug || on !== true) return;
    try {
      const all = await voiceSurveyApi.list(slug);
      setList(all);
      setChosen((id) => id ?? all.find((s) => s.status === "open")?.id ?? all[0]?.id ?? null);
    } catch (err) {
      setError(errorText(err));
    }
  }, [slug, on]);

  useEffect(() => {
    void load();
  }, [load]);

  // Solange eine Frage läuft, alle 20 Sekunden nachsehen – dann wächst der Eingang mit.
  const running = useMemo(() => list.some((s) => s.status === "open"), [list]);
  useEffect(() => {
    if (timer.current) window.clearInterval(timer.current);
    timer.current = window.setInterval(() => void load(), running ? 20_000 : 60_000);
    return () => { if (timer.current) window.clearInterval(timer.current); };
  }, [load, running]);

  const loadDetail = useCallback(async (): Promise<void> => {
    if (!chosen) { setDetail(null); return; }
    try {
      setDetail(await voiceSurveyApi.get(chosen));
    } catch (err) {
      setError(errorText(err));
    }
  }, [chosen]);

  useEffect(() => {
    void loadDetail();
  }, [loadDetail, list]);

  const act = async (what: () => Promise<unknown>, message: string): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await what();
      setNotice(message);
      await load();
      await loadDetail();
    } catch (err) {
      setError(errorText(err));
      setNotice(null);
    } finally {
      setBusy(false);
    }
  };

  /** Freigegebene Antworten in der Reihenfolge, in der sie in den Block kommen. */
  const approved = useMemo(() => {
    const rows = (detail?.answers ?? []).filter((a) => a.status === "approved");
    return [...rows].sort((a, b) => {
      const pa = a.position ?? Number.MAX_SAFE_INTEGER;
      const pb = b.position ?? Number.MAX_SAFE_INTEGER;
      return pa !== pb ? pa - pb : a.created_at.localeCompare(b.created_at);
    });
  }, [detail]);

  const move = (answerId: string, direction: -1 | 1): void => {
    const ids = approved.map((a) => a.id);
    const from = ids.indexOf(answerId);
    const to = from + direction;
    if (from < 0 || to < 0 || to >= ids.length) return;
    [ids[from], ids[to]] = [ids[to]!, ids[from]!];
    void act(() => voiceSurveyApi.setOrder(detail!.id, ids), "Reihenfolge gespeichert.");
  };

  if (on === false) {
    return (
      <ModulAus title="Frage der Woche" icon="mic">
        „Frage der Woche“ ist nicht eingeschaltet. Das geht unter Aussehen → Mitmach-Module.
      </ModulAus>
    );
  }

  const offen = list.find((s) => s.status === "open");
  const neue = detail?.answers.filter((a) => a.status === "new").length ?? 0;
  const stand = { draft: "entwurf", open: "aktiv", closed: "beendet" } as const;

  return (
    <Page className="max-w-6xl">
      <PageHeader
        eyebrow="Hörer"
        title="Frage der Woche"
        description={<>
          Eine Frage stellen, Antworten der Hörer anhören und freigeben, daraus einen O-Ton-Block
          für die Sendung bauen. Jede Antwort ist höchstens {maxSeconds} Sekunden lang.
          {publicAnswers
            ? " Freigegebene Antworten stehen auch auf der Hörerseite – aber nur, wenn der Hörer das ausdrücklich erlaubt hat."
            : " Antworten stehen nicht öffentlich auf der Hörerseite."}
          {!mayRun && " Anlegen, freigeben und starten dürfen nur Moderation und Admins."}
        </>}
        actions={<KanalWahl channels={channels} slug={slug} onChange={(v) => { setSlug(v); setChosen(null); }} />}
      />

      <Meldungen error={error} notice={notice} onClear={() => setNotice(null)} />

      <div className="grid items-start gap-5 lg:grid-cols-[22rem_minmax(0,1fr)]">
        <div className="min-w-0 space-y-5">
          {/* ---------- neue Frage ---------- */}
          <form
            className="card space-y-3 p-5"
            onSubmit={(e) => {
              e.preventDefault();
              void act(() => voiceSurveyApi.create(slug, { question, run_days: runDays }), "Frage angelegt. Jetzt könnt ihr sie starten.");
              setQuestion("");
            }}
          >
            <div className="flex items-center gap-2">
              <span className="flex h-7 w-7 items-center justify-center rounded-md bg-brand-primary/10 text-brand-primary"><Sym name="plus" className="h-4 w-4" /></span>
              <h2 className="card-title">Neue Frage</h2>
            </div>
            <textarea value={question} onChange={(e) => setQuestion(e.target.value)} maxLength={300} rows={2}
              placeholder="Was ist für euch der schönste Platz in der Eifel?"
              className="input resize-none" aria-label="Frage" />
            <div className="flex flex-wrap items-end gap-3">
              <label className="text-[0.8rem] font-medium text-secondary">
                Laufzeit
                <span className="mt-1.5 flex items-center gap-2">
                  <input type="number" min={1} max={60} value={runDays}
                    onChange={(e) => setRunDays(Math.max(1, Math.min(60, Number(e.target.value) || 7)))}
                    className="input w-20" />
                  <span className="font-normal text-muted">Tage</span>
                </span>
              </label>
              <button type="submit" disabled={busy || !mayRun || question.trim().length < 5}
                className="btn btn-primary ml-auto">Anlegen</button>
            </div>
            {offen && <p className="field-hint">Es läuft schon eine Frage – erst beenden, dann die nächste starten.</p>}
          </form>

          {/* ---------- Fragen ---------- */}
          <div>
            <div className="section-label mb-2">Fragen</div>
            {list.length === 0 && (
              <div className="card p-2">
                <EmptyState icon={<Sym name="mic" className="h-5 w-5" />} title="Noch nichts da." description="Legt oben eine Frage an." />
              </div>
            )}
            <ul className="space-y-2">
              {list.map((s) => (
                <li key={s.id}
                  className={`card cursor-pointer p-3.5 transition-shadow hover:shadow-md ${s.id === chosen ? "ring-2 ring-brand-primary/70" : ""}`}
                  onClick={() => setChosen(s.id)}>
                  <button onClick={() => setChosen(s.id)} className="block w-full text-left text-sm font-medium leading-snug text-primary" style={{ minHeight: 0 }}>{s.question}</button>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-muted">
                    <StandSchild stand={stand[s.status]}>
                      {STATUS_TEXT[s.status]}
                      {s.status === "open" && s.closes_at && ` bis ${datum(s.closes_at)}`}
                    </StandSchild>
                    <span>{s.answers_total === 1 ? "1 Antwort" : `${s.answers_total} Antworten`}</span>
                    {s.answers_new > 0 && <Badge tone="info">{s.answers_new} neu</Badge>}
                  </div>
                  {s.cut && (
                    <p className="mt-2 flex items-center gap-1.5 text-xs text-emerald-700 dark:text-emerald-300">
                      <Sym name="music" className="h-3.5 w-3.5" />O-Ton-Block liegt in der Mediathek ({mmss(s.cut.duration_ms)}).
                    </p>
                  )}
                  <div className="mt-3 flex flex-wrap items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    {s.status === "draft" && (
                      <button disabled={busy || !mayRun} onClick={() => void act(() => voiceSurveyApi.open(s.id), "Die Frage läuft.")}
                        className="btn btn-primary btn-sm"><Sym name="play" className="h-3 w-3" />Starten</button>
                    )}
                    {s.status === "open" && (
                      <button disabled={busy || !mayRun} onClick={() => void act(() => voiceSurveyApi.close(s.id), "Frage beendet.")}
                        className="btn btn-secondary btn-sm">Beenden</button>
                    )}
                    {s.status !== "open" && (
                      <button disabled={busy || !mayRun} onClick={() => void act(() => voiceSurveyApi.remove(s.id), "Gelöscht.")}
                        className="btn btn-ghost btn-sm ml-auto"><Sym name="trash" className="h-3.5 w-3.5" />löschen</button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* ---------- Eingang ---------- */}
        {detail ? (
          <Card
            padded={false}
            className="min-w-0"
            title={<span className="flex items-center gap-2"><Sym name="inbox" className="h-4 w-4 text-muted" />Eingang</span>}
            description={detail.question}
            actions={<span className="flex flex-wrap items-center justify-end gap-1.5">
              {neue > 0 ? <Badge tone="info" dot>{neue} noch nicht angehört</Badge> : <Badge tone="success">alles angehört</Badge>}
              {detail.withdrawn > 0 && <Badge>{detail.withdrawn} zurückgezogen</Badge>}
            </span>}
          >
            {detail.answers.length === 0 && (
              <div className="p-5">
                <EmptyState icon={<Sym name="mic" className="h-5 w-5" />} title="Noch keine Antworten."
                  description="Sobald jemand antwortet, steht sie hier." />
              </div>
            )}

            <ul className="divide-y divide-default">
              {detail.answers.map((a) => {
                const rank = a.status === "approved" ? approved.findIndex((x) => x.id === a.id) : null;
                return (
                  <AnswerRow
                    key={a.id}
                    answer={a}
                    busy={busy || !mayRun}
                    rank={rank === -1 ? null : rank}
                    count={approved.length}
                    onPatch={(patch) => void act(
                      () => voiceSurveyApi.patchAnswer(a.id, patch),
                      patch.status === "approved" ? "Freigegeben." : patch.status === "rejected" ? "Abgelehnt, die Aufnahme ist gelöscht." : "Gespeichert."
                    )}
                    onBlock={() => void act(() => voiceSurveyApi.blockAnswer(a.id), "Gerät gesperrt.")}
                    onMove={(d) => move(a.id, d)}
                  />
                );
              })}
            </ul>

            {/* ---------- O-Ton-Block ---------- */}
            <div className="border-t border-default bg-surface-sunken/60 p-5">
              <div className="flex items-center gap-2">
                <Sym name="music" className="h-4 w-4 text-brand-primary" />
                <h3 className="text-sm font-semibold text-primary">O-Ton-Block bauen</h3>
                <Badge tone={approved.length > 0 ? "success" : "neutral"}>{approved.length} freigegeben</Badge>
              </div>
              <p className="mt-2 text-sm leading-relaxed text-muted">
                Die {approved.length === 1 ? "freigegebene Antwort" : `${approved.length} freigegebenen Antworten`} kommen in der
                Reihenfolge oben hintereinander in eine Datei – gleichmäßig ausgesteuert, mit einer kurzen Pause dazwischen.
                Die Datei landet in der Mediathek; von dort plant ihr sie in eine Sendung ein.
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-2 text-sm text-secondary">
                  Pause
                  <input type="number" min={200} max={3000} step={100} value={pauseMs}
                    onChange={(e) => setPauseMs(Math.max(200, Math.min(3000, Number(e.target.value) || 700)))}
                    className="input h-9 w-24" />
                  <span className="text-muted">ms</span>
                </label>
                <button
                  disabled={busy || !mayRun || approved.length === 0}
                  onClick={() => void act(async () => {
                    const r = await voiceSurveyApi.buildCut(detail.id, pauseMs);
                    setNotice(r.message);
                  }, "O-Ton-Block gebaut.")}
                  className="btn btn-primary"
                >
                  <Sym name="sparkles" />{detail.cut ? "Neu bauen" : "O-Ton-Block bauen"}
                </button>
                {approved.length === 0 && <span className="text-xs text-muted">Erst eine Antwort freigeben.</span>}
                {detail.cut && (
                  <span className="text-xs text-muted">
                    Zuletzt gebaut am {datum(detail.cut.built_at)} · {mmss(detail.cut.duration_ms)}
                  </span>
                )}
              </div>
            </div>
          </Card>
        ) : (
          <div className="card p-2">
            <EmptyState icon={<Sym name="inbox" className="h-5 w-5" />} title="Eingang"
              description="Links eine Frage wählen – hier stehen dann ihre Antworten zum Anhören und Freigeben." />
          </div>
        )}
      </div>
    </Page>
  );
}
